import Icon from "components/icon";
import { getAssetIconComponent } from "@/data/assetMapping";
import {
  defineField,
  type FormFieldDefinition,
  type FormFieldInstance,
} from "@/form/useForm";
import { prettyAssetType } from "@/utils/asset";
import { twColor } from "@/utils/style";
import { EAssetType } from "findef";
import { computed, defineComponent, unref } from "vue";
import type {
  IAssetKind,
  UseAssetFormComputedData,
  UseAssetFormState,
} from "../types";
import { tv } from "tailwind-variants";
import Bubbletip from "components/bubbletip";
import { range } from "math";
import { useResponsive } from "~/composables/useResponsive";

const ListItemClass = tv({
  slots: {
    base: "cursor-pointer select-none px-4 py-2 bg-gray-100 outline-solid outline-1 outline-gray-300 inline-flex justify-start items-center gap-3 text-gray-500 hover:bg-primary-100 hover:text-primary-500 hover:outline-primary-500 transition-colors relative",
    title:
      "flex-1 inline-flex flex-col justify-start items-start gap-1 text-sm font-semibold font-['Inter'] leading-tight",
    infoIconWrapper: "grid items-center justify-center",
  },
  variants: {
    selected: {
      true: {
        base: "bg-primary-100 text-primary-500 outline-primary-500",
      },
    },
    disabled: {
      true: {
        base: "opacity-50 cursor-not-allowed",
      },
    },
  },
});

const SelectAssetTypeComponent = defineComponent<{
  dataRef?: FormFieldInstance;
  value?: IAssetKind;
  type?: any;
}>(
  (props) => {
    const responsive = useResponsive();

    const assetTypes: IAssetKind[] = [
      { assetType: EAssetType.EQUITY, listed: true },
      { assetType: EAssetType.EQUITY, listed: false },
      { assetType: EAssetType.REAL_ESTATE, listed: false },
      { assetType: EAssetType.COMMODITY, listed: false },
      { assetType: EAssetType.CRYPTO, listed: false },
      { assetType: EAssetType.ALTERNATIVE, listed: false },
      { assetType: EAssetType.CASH, listed: false },
      { assetType: EAssetType.DEBT, listed: false, disabled: true },
    ];

    const currentValue = computed(
      () =>
        (unref(props.value) ||
          props.dataRef?.value?.value ||
          assetTypes[0]) as IAssetKind,
    );

    const handleClickItem = (item: IAssetKind) => {
      if (item.disabled) return;
      if (props.dataRef) {
        props.dataRef.setValue(item);
      }
    };

    const itemRefs = range(assetTypes.length).map(() => ref());

    return () => {
      return (
        <div>
          <div class="grid grid-cols-2 gap-y-2 gap-x-4">
            {assetTypes.map((it, i) => {
              const isSelected =
                it.assetType === currentValue.value.assetType &&
                it.listed === currentValue.value.listed;
              const prettyType = prettyAssetType(
                it.assetType,
                it.listed,
                undefined,
                false,
                true,
              );
              const IconComponent = getAssetIconComponent(
                { type: it.assetType, listed: !!it.listed },
                { fill: "currentColor", size: "20px" },
              );
              const classes = ListItemClass({
                selected: isSelected,
                disabled: !!it.disabled,
              });
              return (
                <div
                  ref={itemRefs[i]}
                  class={classes.base()}
                  key={`asset-selection-${it.assetType}-${it.listed}`}
                  onClick={() => handleClickItem(it)}
                >
                  <div class="grid items-center justify-center">
                    <IconComponent />
                  </div>
                  <div class={classes.title()}>
                    <div class="justify-start">{prettyType}</div>
                  </div>
                  {!responsive.isSmall.value &&
                    <div class={classes.infoIconWrapper()}>
                      <div class="grid items-center justify-center">
                        <Icon
                          src={async () =>
                            import("assets/img/svg/icons/info-circle.svg?raw")
                          }
                          size="12px"
                          stroke="none"
                          fill={twColor("gray-500")}
                        />
                      </div>
                    </div>
                  }
                </div>
              );
            })}
          </div>
          {assetTypes
            .map((it, i) => {
              return it.disabled ? (
                <Bubbletip
                  triggerRef={itemRefs[i]}
                  text="Coming Soon"
                  variant="white"
                />
              ) : null;
            })
            .filter((it) => it !== null)}
        </div>
      );
    };
  },
  { props: ["dataRef", "value", "type"] },
);

export const selectAssetTypeField = (
  initialValue?: IAssetKind,
): FormFieldDefinition<
  UseAssetFormState,
  IAssetKind,
  UseAssetFormComputedData
> => {
  return defineField({
    type: "dbObject",
    component: (props) => (
      <SelectAssetTypeComponent
        dataRef={props.dataRef}
        value={props.value || { assetType: EAssetType.EQUITY, listed: true }}
      />
    ),
    value: initialValue || { assetType: EAssetType.EQUITY, listed: true },
  });
};
